import { useCallback } from 'react';
import { useAppStore } from '@/stores/appStore';
import type { Point, SnapResult, SnapPoint, ElementPosition } from '@/types/alignment';

/**
 * Hook for alignment and snapping functionality
 */
export function useAlignmentHelpers() {
  const snapEnabled = useAppStore(state => state.alignment.snapEnabled);
  const snapThreshold = useAppStore(state => state.alignment.snapThreshold);
  const showGuides = useAppStore(state => state.alignment.showGuides);
  const viewport = useAppStore(state => state.floorPlan.viewport);

  /**
   * Calculate snap points for a given position
   * Returns snapped position if within threshold, otherwise returns original position
   *
   * IMPORTANT: All coordinates are in IMAGE space (not screen space)
   * The threshold needs to be adjusted based on zoom level to maintain consistent screen-space snapping
   */
  const calculateSnap = useCallback((
    draggedElement: ElementPosition,
    allElements: ElementPosition[],
    currentPosition: Point
  ): SnapResult => {
    if (!snapEnabled) {
      return {
        snapped: false,
        x: currentPosition.x,
        y: currentPosition.y,
        snapPoints: [],
      };
    }

    // CRITICAL: Convert screen-space threshold to image-space threshold
    // The base threshold is in screen pixels, we need to divide by scale to get image pixels
    // At 50% zoom: 10 screen pixels = 20 image pixels
    // At 100% zoom: 10 screen pixels = 10 image pixels
    // At 200% zoom: 10 screen pixels = 5 image pixels
    const imageSpaceThreshold = snapThreshold / viewport.scale;

    const snapPoints: SnapPoint[] = [];
    let snappedX = currentPosition.x;
    let snappedY = currentPosition.y;
    let hasSnappedX = false;
    let hasSnappedY = false;

    // Get element bounds
    const draggedBounds = {
      left: currentPosition.x,
      right: currentPosition.x + draggedElement.width,
      centerX: currentPosition.x + draggedElement.width / 2,
      top: currentPosition.y,
      bottom: currentPosition.y + draggedElement.height,
      centerY: currentPosition.y + draggedElement.height / 2,
    };

    // Check snapping against all other elements
    for (const element of allElements) {
      // Skip the dragged element itself
      if (element.id === draggedElement.id) continue;

      const elementBounds = {
        left: element.x,
        right: element.x + element.width,
        centerX: element.x + element.width / 2,
        top: element.y,
        bottom: element.y + element.height,
        centerY: element.y + element.height / 2,
      };

      // Check horizontal alignment (vertical guides)
      if (!hasSnappedX) {
        // Left edge to left edge
        const leftToLeft = Math.abs(draggedBounds.left - elementBounds.left);
        if (leftToLeft < imageSpaceThreshold) {
          snappedX = elementBounds.left;
          hasSnappedX = true;
          // For aligned edges, show the perpendicular gap (vertical distance between elements)
          const verticalGap = Math.abs(draggedBounds.centerY - elementBounds.centerY);
          snapPoints.push({
            point: { x: elementBounds.left, y: draggedBounds.centerY },
            type: 'edge',
            axis: 'x',
            distance: verticalGap,
            sourceId: element.id,
          });
        }

        // Right edge to right edge
        const rightToRight = Math.abs(draggedBounds.right - elementBounds.right);
        if (!hasSnappedX && rightToRight < imageSpaceThreshold) {
          snappedX = elementBounds.right - draggedElement.width;
          hasSnappedX = true;
          const verticalGap = Math.abs(draggedBounds.centerY - elementBounds.centerY);
          snapPoints.push({
            point: { x: elementBounds.right, y: draggedBounds.centerY },
            type: 'edge',
            axis: 'x',
            distance: verticalGap,
            sourceId: element.id,
          });
        }

        // Center to center
        const centerToCenter = Math.abs(draggedBounds.centerX - elementBounds.centerX);
        if (!hasSnappedX && centerToCenter < imageSpaceThreshold) {
          snappedX = elementBounds.centerX - draggedElement.width / 2;
          hasSnappedX = true;
          snapPoints.push({
            point: { x: elementBounds.centerX, y: draggedBounds.centerY },
            type: 'center',
            axis: 'x',
            distance: 0, // Center alignment - no gap
            sourceId: element.id,
          });
        }

        // Left edge to right edge (gap between elements)
        const leftToRight = Math.abs(draggedBounds.left - elementBounds.right);
        if (!hasSnappedX && leftToRight < imageSpaceThreshold) {
          snappedX = elementBounds.right;
          hasSnappedX = true;
          snapPoints.push({
            point: { x: elementBounds.right, y: draggedBounds.centerY },
            type: 'edge',
            axis: 'x',
            distance: 0, // Elements are touching
            sourceId: element.id,
          });
        }

        // Right edge to left edge (gap between elements)
        const rightToLeft = Math.abs(draggedBounds.right - elementBounds.left);
        if (!hasSnappedX && rightToLeft < imageSpaceThreshold) {
          snappedX = elementBounds.left - draggedElement.width;
          hasSnappedX = true;
          snapPoints.push({
            point: { x: elementBounds.left, y: draggedBounds.centerY },
            type: 'edge',
            axis: 'x',
            distance: 0, // Elements are touching
            sourceId: element.id,
          });
        }
      }

      // Check vertical alignment (horizontal guides)
      if (!hasSnappedY) {
        // Top edge to top edge
        const topToTop = Math.abs(draggedBounds.top - elementBounds.top);
        if (topToTop < imageSpaceThreshold) {
          snappedY = elementBounds.top;
          hasSnappedY = true;
          const horizontalGap = Math.abs(draggedBounds.centerX - elementBounds.centerX);
          snapPoints.push({
            point: { x: draggedBounds.centerX, y: elementBounds.top },
            type: 'edge',
            axis: 'y',
            distance: horizontalGap,
            sourceId: element.id,
          });
        }

        // Bottom edge to bottom edge
        const bottomToBottom = Math.abs(draggedBounds.bottom - elementBounds.bottom);
        if (!hasSnappedY && bottomToBottom < imageSpaceThreshold) {
          snappedY = elementBounds.bottom - draggedElement.height;
          hasSnappedY = true;
          const horizontalGap = Math.abs(draggedBounds.centerX - elementBounds.centerX);
          snapPoints.push({
            point: { x: draggedBounds.centerX, y: elementBounds.bottom },
            type: 'edge',
            axis: 'y',
            distance: horizontalGap,
            sourceId: element.id,
          });
        }

        // Center to center
        const centerToCenter = Math.abs(draggedBounds.centerY - elementBounds.centerY);
        if (!hasSnappedY && centerToCenter < imageSpaceThreshold) {
          snappedY = elementBounds.centerY - draggedElement.height / 2;
          hasSnappedY = true;
          snapPoints.push({
            point: { x: draggedBounds.centerX, y: elementBounds.centerY },
            type: 'center',
            axis: 'y',
            distance: 0, // Center alignment - no gap
            sourceId: element.id,
          });
        }

        // Top edge to bottom edge (gap between elements)
        const topToBottom = Math.abs(draggedBounds.top - elementBounds.bottom);
        if (!hasSnappedY && topToBottom < imageSpaceThreshold) {
          snappedY = elementBounds.bottom;
          hasSnappedY = true;
          snapPoints.push({
            point: { x: draggedBounds.centerX, y: elementBounds.bottom },
            type: 'edge',
            axis: 'y',
            distance: 0, // Elements are touching
            sourceId: element.id,
          });
        }

        // Bottom edge to top edge (gap between elements)
        const bottomToTop = Math.abs(draggedBounds.bottom - elementBounds.top);
        if (!hasSnappedY && bottomToTop < imageSpaceThreshold) {
          snappedY = elementBounds.top - draggedElement.height;
          hasSnappedY = true;
          snapPoints.push({
            point: { x: draggedBounds.centerX, y: elementBounds.top },
            type: 'edge',
            axis: 'y',
            distance: 0, // Elements are touching
            sourceId: element.id,
          });
        }
      }

      // Break if both axes are snapped
      if (hasSnappedX && hasSnappedY) break;
    }

    // Distributed spacing detection disabled for now
    // TODO: Re-enable when pattern detection is more reliable

    const result = {
      snapped: hasSnappedX || hasSnappedY,
      x: snappedX,
      y: snappedY,
      snapPoints: showGuides ? snapPoints : [],
    };

    return result;
  }, [snapEnabled, snapThreshold, showGuides, viewport.scale]);

  /**
   * Detect distributed spacing pattern for an element
   * Returns additional snap point if a pattern is found
   */
  const detectDistributedSpacing = useCallback((
    snappedElement: ElementPosition,
    allElements: ElementPosition[],
    axis: 'x' | 'y',
    snapType: string, // The type of snap that occurred (e.g., 'left-to-left', 'top-to-top')
    imageSpaceThreshold: number
  ): SnapPoint | null => {
    const isHorizontal = axis === 'x';

    // Find elements aligned on the same axis as the snapped element
    // Use a more generous threshold for row/column detection (about 3x the element size)
    const alignmentThreshold = Math.max(
      imageSpaceThreshold * 10, // At least 10x snap threshold
      isHorizontal ? (snappedElement.height * 1.5) : (snappedElement.width * 1.5) // Or 1.5x element size
    );

    const alignedElements = allElements.filter(el => {
      if (el.id === snappedElement.id) return false;

      // Skip elements with invalid positions
      if (typeof el.x !== 'number' || typeof el.y !== 'number' ||
          typeof el.width !== 'number' || typeof el.height !== 'number') {
        return false;
      }

      if (isHorizontal) {
        // Check if vertically aligned (similar Y position for horizontal row)
        const centerY1 = el.y + el.height / 2;
        const centerY2 = snappedElement.y + snappedElement.height / 2;
        const yDiff = Math.abs(centerY1 - centerY2);
        return yDiff < alignmentThreshold;
      } else {
        // Check if horizontally aligned (similar X position for vertical column)
        const centerX1 = el.x + el.width / 2;
        const centerX2 = snappedElement.x + snappedElement.width / 2;
        const xDiff = Math.abs(centerX1 - centerX2);
        return xDiff < alignmentThreshold;
      }
    });

    if (alignedElements.length < 2) {
      return null; // Need at least 2 other elements to detect a pattern
    }

    // Sort by position along the axis
    const sorted = [...alignedElements, snappedElement].sort((a, b) => {
      return isHorizontal ? (a.x - b.x) : (a.y - b.y);
    });

    // Find consecutive pairs and their spacing
    // Filter out overlapping elements (spacing < 5px) as they don't represent a valid pattern
    const spacings: number[] = [];
    for (let i = 0; i < sorted.length - 1; i++) {
      const curr = sorted[i];
      const next = sorted[i + 1];
      const spacing = isHorizontal
        ? (next.x + next.width / 2) - (curr.x + curr.width / 2)
        : (next.y + next.height / 2) - (curr.y + curr.height / 2);

      // Skip overlapping or very close elements (< 5px spacing)
      if (Math.abs(spacing) >= 5) {
        spacings.push(spacing);
      }
    }

    // Check if spacings are consistent (within threshold)
    if (spacings.length < 2) {
      return null;
    }

    const avgSpacing = spacings.reduce((sum, s) => sum + s, 0) / spacings.length;

    // Use percentage-based tolerance: 15% of average spacing with small 5px minimum
    // This prevents false positives from too-generous absolute thresholds
    const consistencyThreshold = Math.max(avgSpacing * 0.15, 5);
    const isConsistent = spacings.every(s => Math.abs(s - avgSpacing) < consistencyThreshold);

    if (!isConsistent) {
      return null;
    }

    // Pattern detected! Calculate the next position in the sequence
    const snappedIndex = sorted.findIndex(el => el.id === snappedElement.id);

    // Determine if we should add a guide before or after
    let nextPos: number;
    let referenceElement: ElementPosition;

    if (snappedIndex === 0) {
      // Element is first, suggest position before it
      referenceElement = sorted[0];
      nextPos = isHorizontal
        ? referenceElement.x + referenceElement.width / 2 - avgSpacing
        : referenceElement.y + referenceElement.height / 2 - avgSpacing;
    } else if (snappedIndex === sorted.length - 1) {
      // Element is last, suggest position after it
      referenceElement = sorted[sorted.length - 1];
      nextPos = isHorizontal
        ? referenceElement.x + referenceElement.width / 2 + avgSpacing
        : referenceElement.y + referenceElement.height / 2 + avgSpacing;
    } else {
      // Element is in the middle, no additional guide needed
      return null;
    }

    // Create distributed snap point
    const point: Point = isHorizontal
      ? { x: nextPos, y: snappedElement.y + snappedElement.height / 2 }
      : { x: snappedElement.x + snappedElement.width / 2, y: nextPos };

    return {
      point,
      type: 'distributed',
      axis,
      distance: Math.abs(avgSpacing),
      sourceId: referenceElement.id,
    };
  }, []);

  /**
   * Snap a point to a grid
   */
  const snapToGrid = useCallback((point: Point, gridSize: number): Point => {
    if (!snapEnabled) return point;

    return {
      x: Math.round(point.x / gridSize) * gridSize,
      y: Math.round(point.y / gridSize) * gridSize,
    };
  }, [snapEnabled]);

  return {
    snapEnabled,
    snapThreshold,
    showGuides,
    calculateSnap,
    snapToGrid,
    detectDistributedSpacing,
  };
}
